<template>
  <div
    class="info-hint"
    :class="[`info-hint--${variant}`, { 'info-hint--panel': panel, 'info-hint--with-action': !!$slots.action }]"
  >
    <i v-if="icon" class="info-hint__icon tim-icons" :class="icon"></i>
    <span class="info-hint__content">
      <slot />
    </span>
    <!-- Optional trailing button (e.g. "Restore customer"); centers the row -->
    <span v-if="$slots.action" class="info-hint__action">
      <slot name="action" />
    </span>
  </div>
</template>

<script>
export default {
  name: 'info-hint',
  props: {
    icon: {
      type: String,
      default: 'icon-bulb-63',
    },
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'warning', 'danger', 'success', 'neutral'].includes(v),
    },
    panel: {
      type: Boolean,
      default: false,
      description: 'Rounded bordered panel styling instead of the left-border strip',
    },
  },
}
</script>

<style scoped>
.info-hint {
  display: flex;
  width: fit-content;
  max-width: 100%;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 0;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.5;
  border-radius: 0 4px 4px 0;
}

.info-hint__icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.info-hint__content {
  min-width: 0;
}

/* With a trailing action the button sets the row height, so center the
   icon, text and button on one line instead of pinning the icon to the top */
.info-hint--with-action {
  align-items: center;
}
.info-hint--with-action .info-hint__icon {
  margin-top: 0;
}
.info-hint__action {
  flex-shrink: 0;
  margin-left: 4px;
}

/* Panel form: rounded bordered card, mirrors the campaign audience count panel */
.info-hint--panel {
  width: 100%;
  padding: 0.75rem 1.25rem;
  border-radius: 0.4285rem;
  font-size: 13px;
}
.info-hint--panel.info-hint--primary {
  border: 1px solid rgba(225, 78, 202, 0.35);
  border-left: 1px solid rgba(225, 78, 202, 0.35);
  background: rgba(225, 78, 202, 0.07);
}
.info-hint--panel.info-hint--warning {
  border: 1px solid rgba(255, 178, 54, 0.4);
  border-left: 1px solid rgba(255, 178, 54, 0.4);
  background: rgba(255, 178, 54, 0.08);
}
.info-hint--panel.info-hint--danger {
  border: 1px solid rgba(253, 93, 147, 0.4);
  border-left: 1px solid rgba(253, 93, 147, 0.4);
  background: rgba(253, 93, 147, 0.08);
}
.info-hint--panel.info-hint--success {
  border: 1px solid rgba(79, 209, 197, 0.4);
  border-left: 1px solid rgba(79, 209, 197, 0.4);
  background: rgba(79, 209, 197, 0.08);
}
.info-hint--panel.info-hint--neutral {
  border: 1px solid rgba(125, 125, 125, 0.3);
  border-left: 1px solid rgba(125, 125, 125, 0.3);
  background: rgba(125, 125, 125, 0.06);
}

.info-hint--primary {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(225, 78, 202, 0.06);
  border-left: 2px solid rgba(225, 78, 202, 0.3);
}
.info-hint--primary .info-hint__icon {
  color: #e14eca;
}

.info-hint--warning {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 178, 54, 0.08);
  border-left: 2px solid rgba(255, 178, 54, 0.4);
}
.info-hint--warning .info-hint__icon {
  color: #ffb236;
}

.info-hint--danger {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(253, 93, 147, 0.08);
  border-left: 2px solid rgba(253, 93, 147, 0.4);
}
.info-hint--danger .info-hint__icon {
  color: #fd5d93;
}

.info-hint--success {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(79, 209, 197, 0.08);
  border-left: 2px solid rgba(79, 209, 197, 0.4);
}
.info-hint--success .info-hint__icon {
  color: #4fd1c5;
}

.info-hint--neutral {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(125, 125, 125, 0.06);
  border-left: 2px solid rgba(125, 125, 125, 0.3);
}
.info-hint--neutral .info-hint__icon {
  color: rgba(255, 255, 255, 0.6);
}
</style>

<style>
.white-content .info-hint--primary {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(225, 78, 202, 0.04);
  border-left-color: rgba(225, 78, 202, 0.2);
}
.white-content .info-hint--warning {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(255, 178, 54, 0.06);
  border-left-color: rgba(255, 178, 54, 0.3);
}
.white-content .info-hint--danger {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(253, 93, 147, 0.06);
  border-left-color: rgba(253, 93, 147, 0.3);
}
.white-content .info-hint--success {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(79, 209, 197, 0.06);
  border-left-color: rgba(79, 209, 197, 0.3);
}
.white-content .info-hint--neutral {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(125, 125, 125, 0.04);
  border-left-color: rgba(125, 125, 125, 0.2);
}
.white-content .info-hint--neutral .info-hint__icon {
  color: rgba(0, 0, 0, 0.5);
}
</style>
